import {
  Copy,
  Check,
  Code2,
  ChevronRight,
  EyeOff,
  Download,
  FileSpreadsheet,
  FileText as FileTextIcon,
  Plus,
  GitCompareArrows,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";

const copyBtnClass =
  "flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors rounded hover:bg-muted/80";

const expandBtnBase =
  "flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded transition-colors";

export function CodeBlockCopyButton({
  show,
  isCopied,
  onCopy,
}: {
  show: boolean;
  isCopied: boolean;
  onCopy: () => void;
}) {
  if (!show) return null;
  return (
    <button
      type="button"
      onClick={onCopy}
      className={copyBtnClass}
      title="Copy code"
    >
      {isCopied ? (
        <>
          <Check className="h-3.5 w-3.5" />
          <span>Copied</span>
        </>
      ) : (
        <>
          <Copy className="h-3.5 w-3.5" />
          <span>Copy</span>
        </>
      )}
    </button>
  );
}

export function CodeBlockExpandToggle({
  isExpanded,
  onToggle,
}: {
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`${expandBtnBase} ${
        isExpanded
          ? "text-muted-foreground hover:text-foreground hover:bg-muted/80"
          : "text-primary bg-primary/10 hover:bg-primary/20"
      }`}
      title={isExpanded ? "Hide code" : "Show code"}
    >
      {isExpanded ? (
        <>
          <EyeOff className="h-3.5 w-3.5" />
          <span>Hide Code</span>
        </>
      ) : (
        <>
          <Code2 className="h-3.5 w-3.5" />
          <span>Show Code</span>
        </>
      )}
    </button>
  );
}

export function CodeBlockCollapsedPreview({
  codeString,
  lineCount,
  onToggleExpand,
  containerClassName,
  moreHintClassName,
  showMoreHint,
  previewLabel = "Code preview",
  detailHint,
  ariaLabel,
}: {
  codeString: string;
  lineCount: number;
  onToggleExpand: () => void;
  /** Merged with default prominent styles; use for small overrides only. */
  containerClassName?: string;
  moreHintClassName?: string;
  showMoreHint: boolean;
  /** Short title in the preview chrome (e.g. "Data preview" for CSV blocks). */
  previewLabel?: string;
  /** Optional subtitle (e.g. class name) — improves scanability in long chats. */
  detailHint?: string;
  /** Overrides default accessible name for the expand control. */
  ariaLabel?: string;
}) {
  const defaultAria =
    ariaLabel ??
    (detailHint
      ? `Show full code: ${detailHint}, ${lineCount} lines`
      : `Show full code editor, ${lineCount} lines`);

  return (
    <button
      type="button"
      aria-expanded={false}
      aria-label={defaultAria}
      onClick={onToggleExpand}
      className={cn(
        "group relative w-full text-left rounded-b-md",
        "border-l-4 border-l-primary",
        "bg-muted/40 dark:bg-muted/35",
        "px-3 py-2.5",
        "ring-1 ring-inset ring-border/45",
        "cursor-pointer",
        "transition-[background-color,box-shadow,color] duration-150",
        "hover:bg-muted/65 dark:hover:bg-muted/50",
        "hover:ring-primary/45",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        containerClassName,
      )}
    >
      <div className="flex items-start justify-between gap-2 pb-2 mb-1 border-b border-border/50">
        <div className="min-w-0 flex flex-col gap-0.5">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-foreground tracking-tight">
            <Code2
              className="h-3.5 w-3.5 shrink-0 text-primary"
              aria-hidden
            />
            <span>{previewLabel}</span>
          </span>
          {detailHint ? (
            <span className="text-[10px] text-muted-foreground truncate font-medium">
              {detailHint}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span
            className="tabular-nums rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary"
            aria-hidden
          >
            {lineCount} lines
          </span>
          <ChevronRight
            className="h-4 w-4 text-muted-foreground transition-colors group-hover:text-primary"
            aria-hidden
          />
        </div>
      </div>

      <div className="font-mono text-[11px] leading-5 text-foreground/90 dark:text-foreground/88" aria-hidden>
        {codeString.split("\n").slice(0, 3).map((line, i) => (
          <div key={i} className="truncate leading-5">
            {line || "\u00A0"}
          </div>
        ))}
      </div>
      {showMoreHint && (
        <div
          className={cn(
            "mt-2 text-center text-[10px] font-medium text-primary/90 dark:text-primary/80",
            moreHintClassName,
          )}
        >
          +{lineCount - 3} more lines — click to expand
        </div>
      )}
    </button>
  );
}

export function DataFormatDownloadButtons({
  onDownloadData,
}: {
  onDownloadData: (
    target: "csv" | "json" | "tsv" | "pdf" | "docx" | "raw",
  ) => void;
}) {
  return (
    <>
      <button
        type="button"
        onClick={() => onDownloadData("csv")}
        className="flex items-center gap-1 px-2 py-1 text-xs text-emerald-400 hover:text-emerald-300 transition-colors rounded hover:bg-emerald-500/10"
        title="Download full data as CSV"
      >
        <Download className="h-3.5 w-3.5" />
        <span>CSV</span>
      </button>
      <button
        type="button"
        onClick={() => onDownloadData("pdf")}
        className="flex items-center gap-1 px-2 py-1 text-xs text-rose-400 hover:text-rose-300 transition-colors rounded hover:bg-rose-500/10"
        title="Export full data as PDF"
      >
        <FileTextIcon className="h-3.5 w-3.5" />
        <span>PDF</span>
      </button>
      <button
        type="button"
        onClick={() => onDownloadData("docx")}
        className="flex items-center gap-1 px-2 py-1 text-xs text-indigo-400 hover:text-indigo-300 transition-colors rounded hover:bg-indigo-500/10"
        title="Export full data as DOCX"
      >
        <FileSpreadsheet className="h-3.5 w-3.5" />
        <span>DOCX</span>
      </button>
    </>
  );
}

const compareClass =
  "inline-flex items-center gap-1 px-2 py-1 text-xs text-purple-400 hover:text-purple-300 transition-colors rounded hover:bg-purple-500/10 whitespace-nowrap";
const createClass =
  "inline-flex items-center gap-1 px-2 py-1 text-xs text-emerald-400 hover:text-emerald-300 transition-colors rounded hover:bg-emerald-500/10 whitespace-nowrap";

const addClassTagged =
  "flex items-center gap-1 px-2 py-1 text-xs text-blue-400 hover:text-blue-300 transition-colors rounded hover:bg-blue-500/10";
const addClassPlain =
  "inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-400 hover:text-blue-300 transition-colors rounded hover:bg-blue-500/10 whitespace-nowrap";

export function WorkspaceAndCompareButtons({
  codeName,
  showCompare = !!codeName,
  compareLabel = "Compare",
  compareTitle = "Compare with original from org",
  compareKind = "compare",
  onAddToWorkspace,
  onCompare,
  variant,
}: {
  codeName: string | undefined;
  showCompare?: boolean;
  compareLabel?: string;
  compareTitle?: string;
  compareKind?: "compare" | "create";
  onAddToWorkspace: () => void;
  onCompare: () => void;
  variant: "tagged" | "plain";
}) {
  const addClass = variant === "tagged" ? addClassTagged : addClassPlain;
  return (
    <>
      <button
        type="button"
        onClick={onAddToWorkspace}
        className={addClass}
        title="Open in editor & add to workspace"
      >
        <Plus
          className={
            variant === "plain" ? "h-3.5 w-3.5 flex-shrink-0" : "h-3.5 w-3.5"
          }
        />
        <span>Add to Workspace</span>
      </button>
      {showCompare && (
        <button
          type="button"
          onClick={onCompare}
          className={compareKind === "create" ? createClass : compareClass}
          title={compareTitle}
        >
          {compareKind === "create" ? (
            <Plus className="h-3.5 w-3.5 flex-shrink-0" />
          ) : (
            <GitCompareArrows className="h-3.5 w-3.5 flex-shrink-0" />
          )}
          <span>{compareLabel}</span>
        </button>
      )}
    </>
  );
}

export function StreamingIndicator({ active }: { active: boolean }) {
  if (!active) return null;
  return <Loader2 className="h-3 w-3 animate-spin text-primary" />;
}
